'use-client';
import React, { useRef } from 'react';
import classNames from 'clsx';
import directionStyles from '../../../common-styles/direction.module.scss';
import presetWrapperStyles from '../../../common-styles/presetWrapper.module.scss';
import styles from './styles/Button.module.scss';
import {
  getDataAttributes,
  getQaDataAttributes,
} from '../../../utils/dataUtils';
import { formatClassNames } from '../../../utils/classNames';
import type {
  StylableButtonSemanticClassNames,
  ButtonProps,
} from './Button.types';
import { IconAnimationTriggers } from './Button.types';
import AnimatedButtonIcon from './components/AnimatedButtonIcon';

export type { ButtonProps };
import { TestIds, SEMANTIC_CLASS_NAMES, COMPONENT_TYPE } from './constants';
import {
  useIconAnimation,
  useButtonInteractionState,
  useButtonEventHandlers,
  useButtonA11y,
} from './hooks';
import { useClickAnalyticsReporter } from '../../../utils/hooks/useClickAnalyticsReporter';
import { ClickAnalyticsGroups } from '../../../utils/clickAnalytics';

const Button: React.FC<ButtonProps> = props => {
  const {
    id,
    link,
    type = 'button',
    label = '',
    labelContent,
    disabled: isDisabled = false,
    onClick: propsOnClick,
    className,
    corvid,
    a11y: propsA11y,
    direction,
    elementProps,
    compPreviewState,
    isQaMode,
    fullNameCompType,
    lang,
    ariaAttributes,
    onDblClick,
    onFocus,
    onBlur,
    reportBiOnClick,
    preventLinkNavigation,
    onMouseEnter: propsOnMouseEnter,
    onMouseLeave: propsOnMouseLeave,
    wix,
    hasTransitions = true,
  } = props;

  const presetsWrapperProps = wix?.presetsWrapperProps || {};
  const animatedIconProps = elementProps?.animatedIcon;
  const corvidIconAnimationTriggers = corvid?.iconAnimationTriggers ?? [];
  const defaultTrigger =
    animatedIconProps?.trigger === IconAnimationTriggers.CLICK
      ? IconAnimationTriggers.CLICK
      : IconAnimationTriggers.HOVER;

  const iconAnimationTriggers =
    corvidIconAnimationTriggers.length > 0
      ? corvidIconAnimationTriggers
      : [defaultTrigger];

  const { iconAnimatedRef, onAnimationForward, onAnimationBackward } =
    useIconAnimation(isDisabled);
  const { active, onMouseEnter, onMouseLeave, onIconAnimationClick } =
    useButtonInteractionState(
      iconAnimationTriggers,
      onAnimationForward,
      onAnimationBackward,
      propsOnMouseEnter,
      propsOnMouseLeave,
    );

  const buttonOrLinkRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const semanticClassNames: StylableButtonSemanticClassNames =
    SEMANTIC_CLASS_NAMES;

  const rootClassName = classNames(
    styles.root,
    className,
    styles.button,
    'button',
    !direction && (directionStyles.fallbackDirection ?? styles.rootDirection),
    formatClassNames(semanticClassNames.root),
    {
      disabled: isDisabled,
      [styles.active]: active,
      [styles.hasTransitions]: hasTransitions,
    },
  );

  const labelClassName = classNames(
    'label',
    styles.label,
    formatClassNames(semanticClassNames.buttonLabel),
    elementProps?.label?.className,
  );

  const linkClassName = styles.link;

  const reportClickAnalytics = useClickAnalyticsReporter({
    link,
    elementId: id,
    elementType: COMPONENT_TYPE,
    elementGroup: ClickAnalyticsGroups.Button,
    elementTitle: label,
    details: { isDisabled },
  });

  const { isLink, eventHandlers } = useButtonEventHandlers({
    isDisabled,
    link,
    propsOnClick,
    preventLinkNavigation,
    reportBiOnClick: reportBiOnClick ?? reportClickAnalytics,
    onMouseEnter,
    onMouseLeave,
    onIconAnimationClick,
    onDblClick,
    onFocus,
    onBlur,
  });

  const a11yAttr = useButtonA11y({ propsA11y, ariaAttributes });
  const renderedLabel = labelContent ?? label;

  const wrapperAttributes = {
    id,
    ...getDataAttributes(props),
    ...getQaDataAttributes(isQaMode, fullNameCompType),
    'data-semantic-classname': semanticClassNames.root,
    lang,
    ...(direction && { dir: direction }),
  };

  const renderButtonContent = () => (
    <>
      {renderedLabel && (
        <span className={labelClassName} data-testid={TestIds.buttonLabel}>
          {renderedLabel}
        </span>
      )}
      <AnimatedButtonIcon
        ref={iconAnimatedRef}
        svg={animatedIconProps?.svg}
        duration={animatedIconProps?.duration}
        className={animatedIconProps?.className}
      />
    </>
  );

  const renderButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={rootClassName}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  const renderLinkedButton = () => {
    const linkHref = isDisabled ? undefined : link!.href;
    const linkRel = link!.rel;
    const linkTarget = link!.target;
    const linkPopupId = link!.linkPopupId;
    const anchorDataId = link!.anchorDataId;
    const anchorCompId = link!.anchorCompId;

    return (
      <a
        href={linkHref}
        rel={linkRel}
        target={linkTarget}
        data-popupid={linkPopupId}
        data-anchor={anchorDataId}
        data-anchor-comp-id={anchorCompId}
        className={classNames(linkClassName, rootClassName)}
        data-testid={TestIds.buttonContent}
        {...wrapperAttributes}
        {...a11yAttr}
        {...eventHandlers}
        role={a11yAttr.role}
        tabIndex={a11yAttr.tabIndex}
        ref={buttonOrLinkRef as React.RefObject<HTMLAnchorElement>}
        data-preview={compPreviewState}
      >
        {renderButtonContent()}
      </a>
    );
  };

  const renderDisabledLinkAsButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={classNames(rootClassName)}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      role="link"
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  const renderContent = () => {
    if (isDisabled && isLink) {
      return renderDisabledLinkAsButton();
    }
    return isLink ? renderLinkedButton() : renderButton();
  };

  return (
    <div className={presetWrapperStyles.presetWrapper} {...presetsWrapperProps}>
      {renderContent()}
    </div>
  );
};

export default Button;
